import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, MessageCircle } from 'lucide-react';
import './About.css';

const VALUES = [
  { e:'🌾', t:'Authentic Sourcing',   d:'We work directly with farmers, weavers and artisans to bring genuinely authentic Indian products.' },
  { e:'✅', t:'Quality First',         d:'Every product is quality-checked before it reaches our customers — no compromise, ever.' },
  { e:'🤝', t:'Honest Dealings',       d:'Transparent pricing, clear communication and reliable delivery on every single order.' },
  { e:'🇮🇳', t:'Proudly Indian',       d:'Celebrating the richness of Indian produce, craft and culture — in every product we trade.' },
];

const TEAM = [
  { n:'Rhutuja B.',          r:'Founder & Director',        e:'Yashraj Traders India', i:'R' },
  { n:'Operations Team',     r:'Order Fulfilment',           e:'Pune, Maharashtra',      i:'O' },
  { n:'Sourcing Team',       r:'Product Procurement',        e:'Pan India Network',      i:'S' },
  { n:'Customer Support',    r:'Enquiry & After-sales',      e:'Quick Response',         i:'C' },
];

const MILES = [
  { y:'2016', t:'Yashraj Traders India founded in Pune, Maharashtra by Rhutuja B.' },
  { y:'2017', t:'GST registration secured (27CBFPB6539R1Z2) — fully compliant trading company.' },
  { y:'2018', t:'Launched food & spice range — roasted channa, organic jaggery, Indian spices.' },
  { y:'2020', t:'Added textile range — cotton fabric, yarn, silk sarees and Paithani Saree.' },
  { y:'2022', t:'Introduced handcrafted resin art — unique custom and standard pieces.' },
  { y:'2025', t:'Growing national and international customer base across multiple product categories.' },
];

export default function About() {
  return (
    <div className="page-enter" style={{ paddingTop: 80 }}>
      <section className="page-hero">
        <div className="page-hero__stripe"/><div className="page-hero__dots"/>
        <div className="container page-hero__inner">
          <div className="tag" style={{ marginBottom: 16 }}>Our Story</div>
          <h1 className="page-hero__title">Built on Quality.<br/><em>Driven by Passion.</em></h1>
          <p className="page-hero__desc">
            Founded in 2016 in Pune by Rhutuja B., Yashraj Traders India is a GST-registered import-export company bringing authentic Indian food products, textiles and handcrafted goods to customers nationwide and beyond.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="abt-sec">
        <div className="container abt-grid">
          <div>
            <div className="tag" style={{ marginBottom: 18 }}>Who We Are</div>
            <h2 className="abt-h">Authentic Indian Trade, <em>Done Right</em></h2>
            <p className="body-md" style={{ marginBottom: 14 }}>
              Yashraj Traders India is a GST-registered import-export trading company headquartered in Pune, Maharashtra. Founded by <strong>Rhutuja B.</strong>, we are committed to sourcing and delivering the finest Indian products — from farm-fresh food and spices to quality textiles and unique resin art.
            </p>
            <p className="body-md" style={{ marginBottom: 28 }}>
              We work directly with producers across India to ensure every product meets our quality standards, and we serve customers with transparency, speed and care on every order.
            </p>
            <div className="abt-checks">
              {['GST Registered — 27CBFPB6539R1Z2', 'Pune, Maharashtra Headquartered', 'National & International Trade', 'Direct from Source Producers', 'Quality Checked — Every Product', 'Responsive Customer Service'].map(c => (
                <div key={c} className="abt-check">
                  <CheckCircle2 size={15} color="var(--fire)"/>{c}
                </div>
              ))}
            </div>
          </div>

          {/* Company info panel */}
          <div className="abt-panel">
            <div className="abt-panel__head">Company Profile</div>
            {[
              { l:'Company',   v:'Yashraj Traders India' },
              { l:'Founder',   v:'Rhutuja B.' },
              { l:'Location',  v:'Pune, Maharashtra — India' },
              { l:'GSTIN',     v:'27CBFPB6539R1Z2', mono:true },
              { l:'Phone 1',   v:'+91 77758 92822' },
              { l:'Phone 2',   v:'+91 80878 84319' },
              { l:'WhatsApp',  v:'+91 92263 18469' },
              { l:'Email',     v:'yashrajtradersindia11@gmail.com', small:true },
              { l:'Sectors',   v:'Food · Textiles · Handicrafts' },
              { l:'Trade',     v:'National & International' },
            ].map((r, i) => (
              <div key={i} className="abt-panel__row">
                <span className="abt-panel__label">{r.l}</span>
                <span className={`abt-panel__val ${r.mono?'abt-panel__val--mono':''} ${r.small?'abt-panel__val--small':''}`}>{r.v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══ VISION & MISSION ══ */}
      <section className="vm-section">
        <div className="container">
          <div className="vm-grid">
            <div className="vm-card vm-card--vision">
              <div className="vm-card__icon">🌍</div>
              <div className="vm-card__tag">Our Vision</div>
              <h3 className="vm-card__title">Global Trade Partner</h3>
              <p className="vm-card__text">
                To become a trusted global trade partner delivering quality products across international markets — connecting India's finest with the world.
              </p>
            </div>
            <div className="vm-card vm-card--mission">
              <div className="vm-card__icon">🎯</div>
              <div className="vm-card__tag">Our Mission</div>
              <h3 className="vm-card__title">Reliable Trade Solutions</h3>
              <p className="vm-card__text">
                "To deliver reliable international trade solutions with superior service, timely delivery, and long-term business relationships."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="abt-values">
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <div className="tag tag--ink" style={{ marginBottom:14 }}>What We Stand For</div>
            <h2 className="abt-h">Our <em>Core Values</em></h2>
          </div>
          <div className="vals-grid">
            {VALUES.map((v,i) => (
              <div key={i} className="val-card">
                <div className="val-card__emoji">{v.e}</div>
                <h3 className="val-card__title">{v.t}</h3>
                <p className="val-card__desc">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="abt-timeline">
        <div className="container">
          <div style={{ marginBottom:48 }}>
            <div className="tag" style={{ marginBottom:14 }}>Our Journey</div>
            <h2 className="abt-h">Milestones That <em>Define Us</em></h2>
          </div>
          <div className="timeline">
            {MILES.map((m,i) => (
              <div key={i} className="tl-item">
                <div className="tl-y">{m.y}</div>
                <div className="tl-dot"/>
                <div className="tl-t">{m.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="abt-team">
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <div className="tag" style={{ marginBottom:14 }}>The Team</div>
            <h2 className="abt-h">People Behind <em>Yashraj Traders</em></h2>
          </div>
          <div className="team-grid">
            {TEAM.map((t,i) => (
              <div key={i} className="team-card">
                <div className="team-av" style={{ background:['var(--fire)','var(--ink)','var(--sky)','#84CC16'][i] }}>{t.i}</div>
                <div className="team-n">{t.n}</div>
                <div className="team-r">{t.r}</div>
                <div className="team-e">{t.e}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-cta">
        <div className="container page-cta__inner">
          <h2 className="page-cta__title">Let's Do Business Together</h2>
          <p className="page-cta__sub">Reach out to Rhutuja and the Yashraj team — we respond quickly.</p>
          <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/contact" className="btn btn-white">Send Enquiry <ArrowRight size={15}/></Link>
            <a href="tel:+917775892822" className="btn btn-outline-white"><Phone size={14}/> Call Now</a>
            <a href="https://wa.me/919226318469" className="btn btn-outline-white" target="_blank" rel="noreferrer"><MessageCircle size={14}/> WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
